import { apiClient } from '@littlebird/auth-client';
import { v4 as uuidv4 } from 'uuid';

import { blobUrlCache } from './blob-cache';

import type { JSONContent } from '../components';

interface FileUploadResponse {
  id: string;
  url: string;
  status: string;
  content_type: string;
  filename: string;
}

interface FileDownloadResponse {
  url: string;
}

export async function uploadEditorImage(
  file: File,
  entity?: string,
  entityId?: string
) {
  const id = uuidv4();
  const { width, height } = await getImageDimensions(file);

  const uploadResponse = await apiClient.post<FileUploadResponse>(
    'get_upload_url',
    {
      file_metadata: {
        id,
        filename: file.name,
        content_type: file.type,
        size: file.size,
        width,
        height,
        entity,
        entity_id: entityId,
      },
    }
  );

  const uploadResult = await fetch(uploadResponse.url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });
  if (!uploadResult.ok) {
    throw new Error(
      `Upload failed with status ${uploadResult.status} ${uploadResult.statusText}`
    );
  }

  await apiClient.post(`finalize_upload/${id}`);
  const localUrl = URL.createObjectURL(file);

  return { fileId: id, src: localUrl };
}

export type FileIdResolver = (
  fileId: string,
  onBlobUrl?: (url: string) => void
) => Promise<string>;

export async function resolveFileIdViaDownloadUrl(
  fileId: string,
  getDownloadUrl: (fileId: string) => Promise<string>,
  onBlobUrl?: (url: string) => void
) {
  const cached = blobUrlCache.get(fileId);
  if (cached) {
    onBlobUrl?.(cached);
    return cached;
  }

  const blobUrl = await blobUrlCache.getOrCreate(fileId, async () => {
    const url = await getDownloadUrl(fileId);
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(
        `Download failed with status ${response.status} ${response.statusText}`
      );
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  });

  onBlobUrl?.(blobUrl);
  return blobUrl;
}

/** Presigned download URL for an uploaded editor image's fileId */
async function getFileDownloadUrl(fileId: string): Promise<string> {
  return (await apiClient.get<FileDownloadResponse>(`files/${fileId}`)).url;
}

export const resolveFileId: FileIdResolver = (fileId, onBlobUrl) =>
  resolveFileIdViaDownloadUrl(fileId, getFileDownloadUrl, onBlobUrl);

export function stripImageSrcsForStorage(content: JSONContent) {
  return walkContent(content, (node) => {
    if (node.type === 'image' && node.attrs?.fileId) {
      return { ...node, attrs: { ...node.attrs, src: null } };
    }

    return node;
  });
}

function walkContent(
  node: JSONContent,
  transform: (node: JSONContent) => JSONContent
): JSONContent {
  const transformed = transform(node);

  if (!transformed.content) return transformed;

  return {
    ...transformed,
    content: transformed.content.map((child) => walkContent(child, transform)),
  };
}

function getImageDimensions(
  file: File
): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    if (!file.type.startsWith('image/')) {
      resolve({ width: 0, height: 0 });

      return;
    }

    const img = new Image();

    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => {
      resolve({ width: 0, height: 0 });
      URL.revokeObjectURL(img.src);
    };

    img.src = URL.createObjectURL(file);
  });
}

export function isBlobUrl(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith('blob:');
}
